import { useState } from "react";
import { Card } from "../ui/Card";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { cn } from "../../lib/cn";
import type { ReactNode } from "react";
import type { CopyMealOption, MemoryView } from "../../screens/library-utils";
import { deleteConfirmReducer, kcalLabel } from "../../screens/library-utils";
import type { DeleteConfirmState } from "../../screens/library-utils";

// Library bottom sheets (plan todo 19). The memory form carries the old
// Memory screen's add/edit/delete UX (and its legacy e2e testids) into the
// Library's Memories section. Entity edit / delete-confirm / copy-meal sheets
// serve the saved-meal, custom-food, and recipe sections.

function SheetShell({ children, onClose }: { readonly children: ReactNode; readonly onClose: () => void }) {
  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="fixed inset-x-0 bottom-0 z-50 flex max-h-[85vh] flex-col justify-end p-4">
        <div className="mx-auto w-full max-w-xl overflow-y-auto">
          <Card className="flex flex-col gap-4 p-5" onClick={(e) => e.stopPropagation()}>
            {children}
          </Card>
        </div>
      </div>
    </>
  );
}

function SheetHeader({ title, onClose }: { readonly title: string; readonly onClose: () => void }) {
  return (
    <div className="flex items-center justify-between">
      <h3 className="text-lg font-semibold text-text">{title}</h3>
      <button
        type="button"
        aria-label="Close"
        className="flex h-11 w-11 items-center justify-center rounded-full text-text-muted active:bg-muted select-none"
        onClick={onClose}
      >
        ×
      </button>
    </div>
  );
}

// ── Memory add/edit form (carried over from the deleted Memory screen) ──────

export interface MemorySavePayload {
  readonly id?: string;
  readonly foodName: string;
  readonly calories: number;
  readonly servingSize?: string;
  readonly notes?: string;
}

export interface MemoryFormSheetProps {
  readonly mode: "add" | "edit";
  readonly memory?: MemoryView;
  readonly busy: boolean;
  readonly onSave: (payload: MemorySavePayload) => Promise<boolean> | boolean;
  readonly onDelete?: () => Promise<boolean> | boolean;
  readonly onClose: () => void;
}

export function MemoryFormSheet({ mode, memory, busy, onSave, onDelete, onClose }: MemoryFormSheetProps) {
  const adding = mode === "add";
  const [formName, setFormName] = useState(memory?.foodName ?? "");
  const [formCalories, setFormCalories] = useState(
    memory === undefined ? "" : String(memory.calories),
  );
  const [formServing, setFormServing] = useState(memory?.servingSize ?? "");
  const [formNotes, setFormNotes] = useState(memory?.notes ?? "");
  const [deleteState, setDeleteState] = useState<DeleteConfirmState>("idle");

  const handleSave = async () => {
    const name = formName.trim();
    const calories = Number(formCalories);
    if (!Number.isFinite(calories)) return;
    if (adding && name.length === 0) return;
    console.log("[Frontend][Library] Saving memory", { mode, name, calories });
    const ok = await onSave({
      ...(memory === undefined ? {} : { id: memory._id }),
      foodName: adding ? name : (memory?.foodName ?? name),
      calories,
      servingSize: formServing || undefined,
      notes: formNotes || undefined,
    });
    if (ok) onClose();
  };

  const handleDelete = async () => {
    if (onDelete === undefined) return;
    if (deleteState === "idle") {
      setDeleteState(deleteConfirmReducer(deleteState, "request"));
      return;
    }
    console.log("[Frontend][Library] Deleting memory from edit form", { id: memory?._id });
    const ok = await onDelete();
    if (ok) onClose();
  };

  return (
    <SheetShell onClose={onClose}>
      <SheetHeader title={adding ? "Add Memory" : "Edit Memory"} onClose={onClose} />

      {adding && (
        <Input
          data-testid="memory-add-name"
          label="Food name"
          value={formName}
          onChange={(e) => setFormName(e.target.value)}
          placeholder="e.g. Avocado toast"
        />
      )}

      <Input
        data-testid={adding ? "memory-add-kcal" : "memory-edit-kcal"}
        label="Calories (kcal)"
        type="number"
        inputMode="numeric"
        value={formCalories}
        onChange={(e) => setFormCalories(e.target.value)}
        placeholder="0"
      />

      <Input
        label="Serving size"
        value={formServing}
        onChange={(e) => setFormServing(e.target.value)}
        placeholder="e.g. 1 slice"
      />

      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-text">Notes</label>
        <textarea
          value={formNotes}
          onChange={(e) => setFormNotes(e.target.value)}
          placeholder="Add details..."
          rows={3}
          className={cn(
            "w-full resize-none rounded-xl border border-border bg-surface py-3 px-4 text-base",
            "text-text placeholder:text-text-muted",
            "focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent",
          )}
        />
      </div>

      {deleteState === "confirming" ? (
        <div className="flex flex-col gap-2 rounded-xl bg-danger/10 p-3">
          <p className="text-sm font-medium text-danger">
            Delete this memory? This cannot be undone.
          </p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              className="flex-1"
              onClick={() => setDeleteState(deleteConfirmReducer(deleteState, "cancel"))}
            >
              Cancel
            </Button>
            <Button
              data-testid="confirm-delete-btn"
              variant="danger"
              size="sm"
              className="flex-1"
              loading={busy}
              onClick={handleDelete}
            >
              Delete
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <Button
            data-testid={adding ? "memory-add-save" : "memory-edit-save"}
            variant="primary"
            size="md"
            loading={busy}
            onClick={handleSave}
            disabled={
              adding
                ? formName.trim().length === 0 || !Number.isFinite(Number(formCalories))
                : !Number.isFinite(Number(formCalories))
            }
          >
            {adding ? "Add memory" : "Save"}
          </Button>
          {!adding && onDelete !== undefined && (
            <Button
              data-testid="memory-delete-btn"
              variant="danger"
              size="md"
              onClick={handleDelete}
            >
              Delete
            </Button>
          )}
        </div>
      )}
    </SheetShell>
  );
}

// ── Generic entity edit sheet (saved meal / custom food / recipe) ───────────

export type EntityEditTarget =
  | { readonly kind: "savedMeal"; readonly id: string; readonly name: string }
  | {
      readonly kind: "customFood";
      readonly id: string;
      readonly name: string;
      readonly servingUnit: string;
      readonly calories?: number;
    }
  | {
      readonly kind: "recipe";
      readonly id: string;
      readonly name: string;
      readonly servings?: number;
    };

export interface EntityEditPayload {
  readonly name: string;
  readonly servingUnit?: string;
  readonly calories?: number;
  readonly servings?: number;
}

export interface EntityEditSheetProps {
  readonly target: EntityEditTarget;
  readonly busy: boolean;
  readonly onSave: (payload: EntityEditPayload) => Promise<boolean> | boolean;
  readonly onClose: () => void;
}

const ENTITY_TITLES: Record<EntityEditTarget["kind"], string> = {
  savedMeal: "Rename saved meal",
  customFood: "Edit custom food",
  recipe: "Edit recipe",
};

export function EntityEditSheet({ target, busy, onSave, onClose }: EntityEditSheetProps) {
  const [name, setName] = useState(target.name);
  const [servingUnit, setServingUnit] = useState(
    target.kind === "customFood" ? target.servingUnit : "",
  );
  const [calories, setCalories] = useState(
    target.kind === "customFood" && target.calories !== undefined ? String(target.calories) : "",
  );
  const [servings, setServings] = useState(
    target.kind === "recipe" && target.servings !== undefined ? String(target.servings) : "",
  );

  const parsedCalories = calories.trim().length === 0 ? undefined : Number(calories);
  const parsedServings = servings.trim().length === 0 ? undefined : Number(servings);
  const caloriesValid = parsedCalories === undefined || Number.isFinite(parsedCalories);
  const servingsValid =
    parsedServings === undefined || (Number.isFinite(parsedServings) && parsedServings > 0);
  const canSave =
    name.trim().length > 0 &&
    caloriesValid &&
    servingsValid &&
    (target.kind !== "customFood" || servingUnit.trim().length > 0);

  const handleSave = async () => {
    if (!canSave) return;
    console.log("[Frontend][Library] Saving entity edit", { kind: target.kind, id: target.id });
    const ok = await onSave({
      name: name.trim(),
      ...(target.kind === "customFood"
        ? { servingUnit: servingUnit.trim(), calories: parsedCalories }
        : {}),
      ...(target.kind === "recipe" ? { servings: parsedServings } : {}),
    });
    if (ok) onClose();
  };

  return (
    <SheetShell onClose={onClose}>
      <div data-testid="library-edit-sheet" className="flex flex-col gap-4">
        <SheetHeader title={ENTITY_TITLES[target.kind]} onClose={onClose} />

        <Input
          data-testid="library-edit-name"
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        {target.kind === "customFood" && (
          <>
            <Input
              data-testid="library-edit-serving-unit"
              label="Serving unit"
              value={servingUnit}
              onChange={(e) => setServingUnit(e.target.value)}
              placeholder="e.g. 1 bar"
            />
            <Input
              data-testid="library-edit-kcal"
              label="Calories (kcal, optional)"
              type="number"
              inputMode="numeric"
              value={calories}
              onChange={(e) => setCalories(e.target.value)}
              placeholder="Leave blank when unknown"
              error={caloriesValid ? undefined : "Enter a number or leave blank"}
            />
          </>
        )}

        {target.kind === "recipe" && (
          <Input
            data-testid="library-edit-servings"
            label="Servings"
            type="number"
            inputMode="numeric"
            value={servings}
            onChange={(e) => setServings(e.target.value)}
            placeholder="e.g. 4"
            error={servingsValid ? undefined : "Enter a positive number"}
          />
        )}

        <Button
          data-testid="library-edit-save"
          variant="primary"
          size="md"
          loading={busy}
          disabled={!canSave}
          onClick={handleSave}
        >
          Save
        </Button>
      </div>
    </SheetShell>
  );
}

// ── Generic two-step delete confirm sheet ───────────────────────────────────

export interface DeleteConfirmSheetProps {
  readonly name: string;
  readonly busy: boolean;
  readonly onConfirm: () => Promise<boolean> | boolean;
  readonly onClose: () => void;
}

export function DeleteConfirmSheet({ name, busy, onConfirm, onClose }: DeleteConfirmSheetProps) {
  const [deleteState, setDeleteState] = useState<DeleteConfirmState>("idle");

  const handleDelete = async () => {
    if (deleteState === "idle") {
      setDeleteState(deleteConfirmReducer(deleteState, "request"));
      return;
    }
    console.log("[Frontend][Library] Delete confirmed", { name });
    const ok = await onConfirm();
    if (ok) onClose();
  };

  return (
    <SheetShell onClose={onClose}>
      <div data-testid="library-delete-sheet" className="flex flex-col gap-4">
        <SheetHeader title="Delete" onClose={onClose} />
        <p className="text-sm text-text-muted">
          {deleteState === "confirming"
            ? `Delete "${name}"? This cannot be undone.`
            : `Delete "${name}" from your library?`}
        </p>
        {deleteState === "confirming" ? (
          <div className="flex gap-2">
            <Button
              data-testid="library-delete-cancel-btn"
              variant="secondary"
              size="md"
              className="flex-1"
              onClick={() => setDeleteState(deleteConfirmReducer(deleteState, "cancel"))}
            >
              Cancel
            </Button>
            <Button
              data-testid="library-delete-confirm-btn"
              variant="danger"
              size="md"
              className="flex-1"
              loading={busy}
              onClick={handleDelete}
            >
              Delete
            </Button>
          </div>
        ) : (
          <Button
            data-testid="library-delete-request-btn"
            variant="danger"
            size="md"
            onClick={handleDelete}
          >
            Delete
          </Button>
        )}
      </div>
    </SheetShell>
  );
}

// ── Copy-a-meal sheet (quick action) ────────────────────────────────────────

export interface CopyMealSheetProps {
  readonly meals: readonly CopyMealOption[];
  readonly onPick: (mealType: string) => void;
  readonly onClose: () => void;
}

export function CopyMealSheet({ meals, onPick, onClose }: CopyMealSheetProps) {
  return (
    <SheetShell onClose={onClose}>
      <div data-testid="copy-meal-sheet" className="flex flex-col gap-3">
        <SheetHeader title="Copy a meal from yesterday" onClose={onClose} />
        <p className="text-sm text-text-muted">
          Pick a meal to prefill the review — nothing is logged until you confirm it.
        </p>
        <div className="flex flex-col gap-2">
          {meals.map((meal) => {
            const totalKcal = meal.items.reduce<number | undefined>((sum, item) => {
              if (item.calories === undefined) return sum;
              return (sum ?? 0) + item.calories;
            }, undefined);
            return (
              <button
                key={meal.mealType}
                type="button"
                data-testid={`copy-meal-option-${meal.mealType}`}
                className="flex min-h-[44px] items-center justify-between rounded-xl border border-border bg-surface px-4 py-3 text-left active:scale-[0.98] select-none"
                onClick={() => onPick(meal.mealType)}
              >
                <span className="text-sm font-medium capitalize text-text">{meal.mealType}</span>
                <span className="text-xs text-text-muted">
                  {meal.items.length} item{meal.items.length === 1 ? "" : "s"} · {kcalLabel(totalKcal)}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </SheetShell>
  );
}
